import React, { useState, useEffect, createContext, useContext } from 'react';
import { Trophy, Star, Zap, Shield, Terminal, Coffee, Eye, Scroll, MessageSquare, Lock } from 'lucide-react';

// Achievement Context
const AchievementContext = createContext();

export const useAchievements = () => useContext(AchievementContext);

// All available achievements
const achievementsList = [
  { 
    id: 'first_visit', 
    name: 'First Contact', 
    description: 'Welcome to the cluster!',
    icon: Star,
    color: '#F59E0B',
    secret: false
  },
  { 
    id: 'scroll_explorer', 
    name: 'Scroll Explorer', 
    description: 'Scrolled through 50% of the page',
    icon: Scroll,
    color: '#3B82F6',
    secret: false
  },
  { 
    id: 'terminal_hacker', 
    name: 'Terminal Hacker', 
    description: 'Used the interactive terminal',
    icon: Terminal,
    color: '#10B981',
    secret: false
  },
  { 
    id: 'tried_to_hack', 
    name: 'Nice Try!', 
    description: 'Attempted to hack the cluster',
    icon: Shield,
    color: '#EF4444',
    secret: false
  },
  { 
    id: 'coffee_lover', 
    name: 'Coffee Addict', 
    description: 'Brewed DevOps fuel',
    icon: Coffee,
    color: '#92400E',
    secret: false
  },
  { 
    id: 'contact_hero', 
    name: 'Contact Hero', 
    description: 'Sent a message',
    icon: MessageSquare,
    color: '#8B5CF6',
    secret: false
  },
  { 
    id: 'matrix_mode', 
    name: '🔴 The One', 
    description: 'Discovered the secret code',
    icon: Eye,
    color: '#00FF00',
    secret: true
  },
  { 
    id: 'completionist', 
    name: 'Completionist', 
    description: 'Unlocked 5+ achievements',
    icon: Trophy,
    color: '#F59E0B',
    secret: false
  },
];

// Achievement Toast Notification
const AchievementToast = ({ achievement, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const Icon = achievement.icon;

  return (
    <div className="fixed top-20 right-4 z-[100] animate-slide-in-right">
      <div className="bg-gray-900 border-2 border-yellow-500 rounded-2xl p-4 shadow-2xl flex items-center gap-4 min-w-[300px]">
        <div 
          className="w-14 h-14 rounded-xl flex items-center justify-center animate-bounce"
          style={{ backgroundColor: `${achievement.color}30` }}
        >
          <Icon className="w-7 h-7" style={{ color: achievement.color }} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Trophy className="w-4 h-4 text-yellow-500" />
            <span className="text-yellow-500 text-xs font-bold uppercase">Achievement Unlocked!</span>
          </div>
          <div className="text-white font-bold">{achievement.name}</div>
          <div className="text-gray-400 text-sm">{achievement.description}</div>
        </div>
      </div>
    </div>
  );
};

// Achievement Badge Component
const AchievementBadge = ({ achievement, unlocked }) => {
  const Icon = achievement.icon;
  
  return (
    <div 
      className={`relative p-3 rounded-xl border-2 transition-all duration-300 ${
        unlocked 
          ? 'bg-gray-800 border-gray-600 hover:border-yellow-500' 
          : 'bg-gray-900 border-gray-800 opacity-50'
      }`}
    >
      <div className="flex items-center gap-3">
        <div 
          className={`w-10 h-10 rounded-lg flex items-center justify-center ${unlocked ? '' : 'grayscale'}`}
          style={{ backgroundColor: unlocked ? `${achievement.color}20` : '#1f2937' }}
        >
          {achievement.secret && !unlocked ? (
            <Lock className="w-5 h-5 text-gray-600" />
          ) : (
            <Icon className="w-5 h-5" style={{ color: unlocked ? achievement.color : '#4B5563' }} />
          )}
        </div>
        <div>
          <div className={`font-bold text-sm ${unlocked ? 'text-white' : 'text-gray-600'}`}>
            {achievement.secret && !unlocked ? '???' : achievement.name}
          </div>
          <div className={`text-xs ${unlocked ? 'text-gray-400' : 'text-gray-700'}`}>
            {achievement.secret && !unlocked ? 'Secret achievement' : achievement.description}
          </div>
        </div>
      </div>
      {unlocked && (
        <div className="absolute -top-1 -right-1 w-5 h-5 bg-yellow-500 rounded-full flex items-center justify-center">
          <span className="text-xs">✓</span>
        </div>
      )}
    </div>
  );
};

// Matrix Rain Effect
const MatrixRain = ({ active }) => {
  const [columns, setColumns] = useState([]);

  useEffect(() => {
    if (!active) return;
    
    const chars = 'アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン0123456789KUBERNETES';
    const cols = Math.floor(window.innerWidth / 20);
    
    const initialColumns = Array.from({ length: cols }, () => ({
      chars: Array.from({ length: 25 }, () => chars[Math.floor(Math.random() * chars.length)]),
      y: Math.random() * -100,
      speed: 2 + Math.random() * 3,
    }));
    
    setColumns(initialColumns);

    const interval = setInterval(() => {
      setColumns(prev => prev.map(col => ({
        ...col,
        y: col.y > 120 ? -20 : col.y + col.speed,
        chars: col.chars.map(c => Math.random() > 0.95 ? chars[Math.floor(Math.random() * chars.length)] : c),
      })));
    }, 50);

    return () => clearInterval(interval);
  }, [active]);

  if (!active) return null;

  return (
    <div className="fixed inset-0 z-[90] pointer-events-none overflow-hidden bg-black/80">
      {columns.map((col, i) => (
        <div
          key={i}
          className="absolute text-green-500 font-mono text-sm whitespace-pre leading-5"
          style={{
            left: `${i * 20}px`,
            top: `${col.y}%`,
            textShadow: '0 0 10px #00ff00',
          }}
        >
          {col.chars.map((char, j) => (
            <div 
              key={j} 
              style={{ opacity: 1 - j * 0.04 }}
              className={j === 0 ? 'text-white' : ''}
            >
              {char}
            </div>
          ))}
        </div>
      ))}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
        <div className="text-green-400 text-4xl font-bold mb-4 animate-pulse">MATRIX MODE ACTIVATED</div>
        <div className="text-green-500 text-lg">Welcome to the real world, Neo.</div>
        <div className="text-green-600 text-sm mt-4">Press ESC to exit</div>
      </div>
    </div>
  );
};

// Achievement Provider Component
export const AchievementProvider = ({ children }) => {
  const [unlockedAchievements, setUnlockedAchievements] = useState(() => {
    const saved = localStorage.getItem('devops_achievements');
    return saved ? JSON.parse(saved) : ['first_visit'];
  });
  const [currentToast, setCurrentToast] = useState(null);
  const [matrixMode, setMatrixMode] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const [konamiProgress, setKonamiProgress] = useState(0);

  // Konami Code: ↑↑↓↓←→←→BA
  const konamiCode = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'KeyB', 'KeyA'];

  useEffect(() => {
    localStorage.setItem('devops_achievements', JSON.stringify(unlockedAchievements));
  }, [unlockedAchievements]);

  // Listen for Konami code
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && matrixMode) {
        setMatrixMode(false);
        return;
      }

      const expectedKey = konamiCode[konamiProgress];
      if (e.code === expectedKey) {
        const newProgress = konamiProgress + 1;
        setKonamiProgress(newProgress);
        
        if (newProgress === konamiCode.length) {
          setMatrixMode(true);
          unlockAchievement('matrix_mode');
          setKonamiProgress(0);
        }
      } else {
        setKonamiProgress(0);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [konamiProgress, matrixMode]);

  // Track scroll for achievement
  useEffect(() => {
    const handleScroll = () => {
      const scrollPercent = (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100;
      if (scrollPercent > 50 && !unlockedAchievements.includes('scroll_explorer')) {
        unlockAchievement('scroll_explorer');
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [unlockedAchievements]);

  // Check for completionist
  useEffect(() => {
    if (unlockedAchievements.length >= 5 && !unlockedAchievements.includes('completionist')) {
      setTimeout(() => unlockAchievement('completionist'), 1000);
    }
  }, [unlockedAchievements]);

  const unlockAchievement = (id) => {
    if (unlockedAchievements.includes(id)) return;
    
    const achievement = achievementsList.find(a => a.id === id);
    if (achievement) {
      setUnlockedAchievements(prev => [...prev, id]);
      setCurrentToast(achievement);
    }
  };

  const value = {
    achievements: achievementsList,
    unlockedAchievements,
    unlockAchievement,
    showPanel,
    setShowPanel,
    totalAchievements: achievementsList.length,
    unlockedCount: unlockedAchievements.length,
  };

  return (
    <AchievementContext.Provider value={value}>
      {children}
      {currentToast && (
        <AchievementToast 
          achievement={currentToast} 
          onClose={() => setCurrentToast(null)} 
        />
      )}
      <MatrixRain active={matrixMode} />
    </AchievementContext.Provider>
  );
};

// Achievement Panel Toggle Button
export const AchievementButton = () => {
  const { showPanel, setShowPanel, unlockedCount, totalAchievements } = useAchievements();

  return (
    <button
      onClick={() => setShowPanel(!showPanel)}
      className="fixed bottom-6 right-24 z-50 w-14 h-14 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-full shadow-lg hover:scale-110 transition-transform flex items-center justify-center group"
    >
      <Trophy className="w-6 h-6 text-white" />
      <div className="absolute -top-1 -right-1 w-6 h-6 bg-purple-600 rounded-full text-white text-xs font-bold flex items-center justify-center">
        {unlockedCount}
      </div>
      <div className="absolute right-full mr-3 bg-gray-900 text-white text-sm px-3 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
        {unlockedCount}/{totalAchievements} Achievements
      </div>
    </button>
  );
};

// Achievement Panel
export const AchievementPanel = () => {
  const { achievements, unlockedAchievements, showPanel, setShowPanel, unlockedCount, totalAchievements } = useAchievements();

  if (!showPanel) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowPanel(false)} />
      <div className="relative bg-gray-900 rounded-3xl p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto border border-gray-700 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl flex items-center justify-center">
              <Trophy className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-white font-bold text-xl">Achievements</h3>
              <p className="text-gray-400 text-sm">{unlockedCount}/{totalAchievements} unlocked</p>
            </div>
          </div>
          <button 
            onClick={() => setShowPanel(false)}
            className="text-gray-400 hover:text-white transition-colors text-2xl"
          >
            ×
          </button>
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="h-3 bg-gray-800 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-yellow-500 to-orange-500 transition-all duration-500"
              style={{ width: `${(unlockedCount / totalAchievements) * 100}%` }}
            />
          </div>
        </div>

        {/* Achievement Grid */}
        <div className="space-y-3">
          {achievements.map(achievement => (
            <AchievementBadge 
              key={achievement.id}
              achievement={achievement}
              unlocked={unlockedAchievements.includes(achievement.id)}
            />
          ))}
        </div>

        {/* Secret Hint */}
        <div className="mt-6 p-3 bg-gray-800/50 rounded-xl border border-gray-700">
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <Zap className="w-4 h-4" />
            <span>Hint: Old-school gamers know the secret code... ↑↑↓↓</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AchievementProvider;
